import { getMapboxUrl } from "@inrange/building-manager-api-client";
import {
  Building as ApiBuilding,
  getBuildingCoordinates,
} from "@inrange/building-manager-api-client/models-site";
import L, { latLngBounds, LatLngBounds, Map as LeafletMap } from "leaflet";
import "leaflet-editable";
import "leaflet.path.drag";
import "leaflet/dist/leaflet.css";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { GeoJSON, MapContainer, TileLayer } from "react-leaflet";
import ReactLeafletGoogleLayer from "react-leaflet-google-layer";
import {
  GMAPS_API_KEY,
  testMode,
} from "../../site/siteSections/PVCustomObjects/pvcustomobjectmap-utils";
import { MapHook } from "../../site/siteSections/PVCustomObjects/MapHook";

/**
 * The site-edit BuildingMap stores edit-mode audit fields
 * (`customised`, `sourceGeometry`, `sourcePolygonType`) that the
 * canonical api-client Building type doesn't expose, but the BE
 * round-trips. We mirror that pattern here so saved polygons carry
 * the same provenance metadata as ones edited from the site page.
 */
export type EditableBuilding = ApiBuilding & {
  customised?: boolean;
  sourceGeometry?: ApiBuilding["geometry"];
  sourcePolygonType?: string;
};

const editableYellowStyle: L.PathOptions = {
  color: "#f5b300",
  fillOpacity: 0.1,
  weight: 4,
  opacity: 1,
};

interface BoundaryEditorMapProps {
  siteCenter: [number, number];
  buildings: EditableBuilding[];
  onChange: (buildings: Record<string, EditableBuilding>) => void;
  /**
   * Fires when the underlying Leaflet map mounts (and again with
   * null on unmount). The bulk-review per-site item uses this to
   * drive the Y/T zoom keyboard shortcuts from outside the map.
   */
  onMapReady?: (map: LeafletMap | null) => void;
}

/**
 * Drag-editable map showing one site's building polygons. Mirrors the
 * edit-mode UX of BuildingMap.tsx on the site-edit page but with a
 * narrower prop surface so it can be mounted inside the bulk-review
 * surface without the site-list / OSM-suggestions baggage.
 *
 * Edited polygons flow back up via onChange every time the operator
 * finishes a drag. The parent decides when to persist - this
 * component just reports the current editable-layer state.
 */
const BoundaryEditorMap: React.FC<BoundaryEditorMapProps> = ({
  siteCenter,
  buildings,
  onChange,
  onMapReady,
}) => {
  const [map, setMap] = useState<LeafletMap | null>(null);

  useEffect(() => {
    if (!onMapReady) return;
    onMapReady(map);
    return () => onMapReady(null);
  }, [map, onMapReady]);
  const buildingsRef = useRef<Record<string, EditableBuilding>>({});

  useEffect(() => {
    const seeded: Record<string, EditableBuilding> = {};
    buildings.forEach((b, ii) => {
      const key = b.id ?? `${b.geometry?.type}-${ii}`;
      seeded[key] = b;
    });
    buildingsRef.current = seeded;
  }, [buildings]);

  const handleEdit = useCallback(
    (e: any) => {
      const layer = e.layer;
      const key = layer.options.id;
      const prev = buildingsRef.current[key];
      if (!prev) return;
      const geometry = layer.toGeoJSON().geometry;
      const next: EditableBuilding = {
        ...prev,
        geometry,
        polygonType: "custom",
        customised: true,
      };
      if (!next.sourcePolygonType) {
        next.sourcePolygonType = prev.polygonType || "unknown";
        next.sourceGeometry = prev.geometry;
      }
      buildingsRef.current = { ...buildingsRef.current, [key]: next };
      onChange(buildingsRef.current);
    },
    [onChange]
  );

  useEffect(() => {
    if (!map) return;
    map.on("editable:vertex:dragend", handleEdit);
    map.on("editable:vertex:deleted", handleEdit);
    map.on("editable:dragend", handleEdit);
    return () => {
      map.off("editable:vertex:dragend", handleEdit);
      map.off("editable:vertex:deleted", handleEdit);
      map.off("editable:dragend", handleEdit);
    };
  }, [map, handleEdit]);

  // Enable edit + drag on every polygon as soon as the map mounts -
  // bulk-review is "always edit" mode, no separate enable click.
  useEffect(() => {
    if (!map) return;
    map.eachLayer((layer: any) => {
      if (layer.options?.id && layer.enableEdit) {
        layer.enableEdit();
        if (layer.dragging?.enable) layer.dragging.enable();
      }
    });
  }, [map, buildings]);

  const bounds: LatLngBounds = latLngBounds([siteCenter]);
  buildings.forEach((building) => {
    const coordinates = getBuildingCoordinates(building);
    coordinates[0].forEach((coord: number[]) => {
      bounds.extend([...coord].reverse() as [number, number]);
    });
  });

  return (
    <div
      data-testid="bulk-review-boundary-editor-map"
      style={{ width: "100%", height: "100%" }}
    >
      <MapContainer
        center={siteCenter}
        style={{ width: "100%", height: "100%", cursor: "grab" }}
        ref={setMap}
        editable={true}
        boxZoom={false}
      >
        <MapHook bounds={bounds} />
        {testMode ? (
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url={getMapboxUrl("satellite-v9")}
          />
        ) : (
          <ReactLeafletGoogleLayerFix
            apiKey={GMAPS_API_KEY}
            type={"satellite"}
          />
        )}
        {buildings.map((building, ii) => {
          const id = building.id ?? `${building.geometry?.type}-${ii}`;
          const data = { ...building.geometry };
          if (typeof data.coordinates === "string") {
            data.coordinates = JSON.parse(data.coordinates) as number[][][];
          }
          return (
            <GeoJSON
              key={`${id}-${buildings.length}`}
              data={data as any}
              style={editableYellowStyle}
              {...({ id, editable: true, draggable: true } as any)}
            />
          );
        })}
      </MapContainer>
    </div>
  );
};

const ReactLeafletGoogleLayerFix =
  ReactLeafletGoogleLayer as React.ComponentType<{
    apiKey: string;
    type: string;
  }>;

export default BoundaryEditorMap;
